import { authStore, type AuthStatus } from '@/lib/authStore';
import { normalizeApiErrorDetail, type ApiErrorKind } from '@/lib/normalizeApiError';

export type SessionState = 'authenticated' | 'unauthenticated' | 'offline' | 'degraded' | 'unknown' | 'loading';

export type AuthSessionEvent =
  | { type: 'LOGIN_SUCCESS'; source: string; at: number; eventId?: string }
  | { type: 'LOGOUT'; source: string; at: number; eventId?: string }
  | { type: 'AUTH_EXPIRED'; status: 401 | 403; source: string; at: number; code?: string; message?: string; eventId?: string }
  | { type: 'AUTH_ERROR'; status: 401 | 403; source: string; at: number; code?: string; message?: string; eventId?: string }
  | { type: 'NETWORK_ERROR'; source: string; at: number; message?: string; eventId?: string }
  | { type: 'OFFLINE_ENTER'; source: string; at: number; eventId?: string }
  | { type: 'OFFLINE_EXIT'; source: string; at: number; eventId?: string }
  | { type: 'SERVER_ERROR'; status: number; source: string; at: number; message?: string; eventId?: string }
  | { type: 'RECOVERED'; source: string; at: number; eventId?: string }
  | { type: 'BRANDING_REFRESHED'; source: string; at: number; eventId?: string };

type SessionListener = (event: AuthSessionEvent) => void;
type CrossTabEventType = 'LOGIN_SUCCESS' | 'LOGOUT' | 'AUTH_EXPIRED' | 'OFFLINE_ENTER' | 'OFFLINE_EXIT' | 'BRANDING_REFRESHED';
type CrossTabSessionEvent = {
  eventId: string;
  type: CrossTabEventType;
  source: string;
  at: number;
  status?: 401 | 403;
  code?: string;
  message?: string;
};

let lastRedirectAt = 0;
let currentState: SessionState = 'unknown';
const listeners = new Set<SessionListener>();
let onlineInitialized = false;
const lastEventByKey = new Map<string, number>();
const processedEventIds = new Set<string>();
let authChannel: BroadcastChannel | null = null;

let lastAuthInvalidAt = 0;
let lastLoginAt = 0;
let lastOfflineAt = 0;

const REDIRECT_GUARD_MS = 2000;
const EVENT_DEDUPE_MS = 2000;
const SESSION_EVENT_STORAGE_KEY = 'auth:session:event';

function mapAuthStatus(status: AuthStatus): SessionState {
  if (status === 'authenticated') return 'authenticated';
  if (status === 'unauthenticated') return 'unauthenticated';
  if (status === 'loading') return 'loading';
  return 'unknown';
}

function notify(event: AuthSessionEvent) {
  listeners.forEach((listener) => listener(event));
}

function clearSessionStorage() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
}

function hasToken(): boolean {
  return !!localStorage.getItem('token');
}

function newEventId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function markEventProcessed(eventId?: string): boolean {
  if (!eventId) return false;
  if (processedEventIds.has(eventId)) return true;
  processedEventIds.add(eventId);
  if (processedEventIds.size > 500) {
    const first = processedEventIds.values().next().value;
    if (first) processedEventIds.delete(first);
  }
  return false;
}

function parseCrossTabEvent(input: unknown): AuthSessionEvent | null {
  if (!input || typeof input !== 'object') return null;
  const data = input as Partial<CrossTabSessionEvent>;
  if (!data.type || !data.source || typeof data.at !== 'number') return null;

  if (data.type === 'AUTH_EXPIRED') {
    if (data.status !== 401 && data.status !== 403) return null;
    return {
      type: 'AUTH_EXPIRED',
      source: `${data.source}:sync`,
      at: data.at,
      status: data.status,
      ...(typeof data.code === 'string' ? { code: data.code } : {}),
      ...(typeof data.message === 'string' ? { message: data.message } : {}),
      ...(typeof data.eventId === 'string' ? { eventId: data.eventId } : {}),
    };
  }

  if (
    data.type === 'LOGIN_SUCCESS' ||
    data.type === 'LOGOUT' ||
    data.type === 'OFFLINE_ENTER' ||
    data.type === 'OFFLINE_EXIT' ||
    data.type === 'BRANDING_REFRESHED'
  ) {
    return {
      type: data.type,
      source: `${data.source}:sync`,
      at: data.at,
      ...(typeof data.eventId === 'string' ? { eventId: data.eventId } : {}),
    };
  }

  return null;
}

function publishCrossTab(event: CrossTabSessionEvent): void {
  const payload = JSON.stringify(event);
  try {
    authChannel?.postMessage(event);
  } catch {
    // no-op
  }
  try {
    localStorage.setItem(SESSION_EVENT_STORAGE_KEY, payload);
  } catch {
    // no-op
  }
}

function shouldBroadcast(event: AuthSessionEvent): boolean {
  return !event.source.includes(':sync');
}

function toCrossTabEvent(event: AuthSessionEvent): CrossTabSessionEvent | null {
  if (event.type === 'LOGIN_SUCCESS' || event.type === 'LOGOUT' || event.type === 'OFFLINE_ENTER' || event.type === 'OFFLINE_EXIT' || event.type === 'BRANDING_REFRESHED') {
    return {
      eventId: event.eventId ?? newEventId(),
      type: event.type,
      source: event.source,
      at: event.at,
    };
  }

  if (event.type === 'AUTH_ERROR' || event.type === 'AUTH_EXPIRED') {
    return {
      eventId: event.eventId ?? newEventId(),
      type: 'AUTH_EXPIRED',
      source: event.source,
      at: event.at,
      status: event.status,
      code: event.code,
      message: event.message,
    };
  }

  return null;
}

function guardedRedirectLogin() {
  const now = Date.now();
  if (now - lastRedirectAt < REDIRECT_GUARD_MS) return;
  lastRedirectAt = now;

  if (window.location.pathname.includes('/login')) return;

  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
  window.location.href = `/login?returnUrl=${returnUrl}`;
}

function setState(next: SessionState, reason: string) {
  currentState = next;
  if (import.meta.env.DEV) {
    console.debug('[sessionCoordinator] state =>', next, reason);
  }
}

function handleEvent(event: AuthSessionEvent) {
  if (event.type === 'LOGIN_SUCCESS') {
    lastLoginAt = Math.max(lastLoginAt, event.at);
    if (hasToken() && lastLoginAt >= lastAuthInvalidAt) {
      setState('authenticated', `${event.source}:login`);
    }
    notify(event);
    return;
  }

  if (event.type === 'LOGOUT') {
    lastAuthInvalidAt = Math.max(lastAuthInvalidAt, event.at);
    setState('unauthenticated', `${event.source}:logout`);
    clearSessionStorage();
    authStore.setUnauthenticated();
    guardedRedirectLogin();
    notify(event);
    return;
  }

  if (event.type === 'AUTH_ERROR' || event.type === 'AUTH_EXPIRED') {
    lastAuthInvalidAt = Math.max(lastAuthInvalidAt, event.at);
    setState('unauthenticated', `${event.source}:${event.status}`);
    clearSessionStorage();
    authStore.setUnauthenticated();
    guardedRedirectLogin();
    notify(event);
    return;
  }

  if (event.type === 'NETWORK_ERROR' || event.type === 'OFFLINE_ENTER') {
    lastOfflineAt = Math.max(lastOfflineAt, event.at);
    setState('offline', event.source);
    notify(event);
    return;
  }

  if (event.type === 'OFFLINE_EXIT') {
    if (!hasToken() || lastAuthInvalidAt > lastLoginAt) {
      setState('unauthenticated', `${event.source}:offline_exit_no_token`);
      authStore.setUnauthenticated();
    } else {
      setState('authenticated', `${event.source}:offline_exit`);
    }
    notify(event);
    return;
  }

  if (event.type === 'SERVER_ERROR') {
    setState('degraded', `${event.source}:${event.status}`);
    notify(event);
    return;
  }

  if (event.type === 'RECOVERED') {
    if (event.at < lastOfflineAt) {
      return;
    }
    const token = hasToken();
    const authState = authStore.getState();
    if (!token || lastAuthInvalidAt > lastLoginAt) {
      setState('unauthenticated', `${event.source}:recovered_no_token_or_invalid`);
      authStore.setUnauthenticated();
    } else if (authState.status !== 'unauthenticated') {
      setState('authenticated', event.source);
    } else {
      setState('unauthenticated', event.source);
    }
    notify(event);
    return;
  }

  if (event.type === 'BRANDING_REFRESHED') {
    notify(event);
  }
}

export function subscribeSessionEvents(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSessionState(): SessionState {
  if (currentState === 'unknown') {
    return mapAuthStatus(authStore.getState().status);
  }
  return currentState;
}

export function emitAuthSessionEvent(event: AuthSessionEvent): void {
  const bucket = Math.floor((event.at || Date.now()) / EVENT_DEDUPE_MS);
  const endpointHint = event.source.split(':')[1] ?? event.source;
  const dedupeKey =
    event.type === 'AUTH_ERROR' || event.type === 'AUTH_EXPIRED'
      ? `${event.type}:${event.status}:${endpointHint}:${bucket}`
      : event.type === 'SERVER_ERROR'
      ? `${event.type}:${event.status}:${endpointHint}:${bucket}`
      : `${event.type}:${endpointHint}:${bucket}`;
  const now = Date.now();
  const last = lastEventByKey.get(dedupeKey) ?? 0;
  if (now - last < EVENT_DEDUPE_MS) return;
  lastEventByKey.set(dedupeKey, now);

  if (markEventProcessed(event.eventId)) return;

  const crossTab = toCrossTabEvent(event);
  if (crossTab && shouldBroadcast(event)) {
    markEventProcessed(crossTab.eventId);
    publishCrossTab(crossTab);
  }

  handleEvent(event);
}

export function initSessionCoordinator(): void {
  if (onlineInitialized || typeof window === 'undefined') return;
  onlineInitialized = true;

  if ('BroadcastChannel' in window) {
    authChannel = new BroadcastChannel('auth-session');
    authChannel.addEventListener('message', (msg: MessageEvent<CrossTabSessionEvent>) => {
      const parsed = parseCrossTabEvent(msg.data);
      if (!parsed) return;
      emitAuthSessionEvent(parsed);
    });
  }

  window.addEventListener('storage', (event) => {
    if (event.key !== SESSION_EVENT_STORAGE_KEY || !event.newValue) return;
    try {
      const parsed = parseCrossTabEvent(JSON.parse(event.newValue));
      if (!parsed) return;
      emitAuthSessionEvent(parsed);
    } catch {
      // no-op
    }
  });

  window.addEventListener('online', () => {
    emitAuthSessionEvent({
      type: 'OFFLINE_EXIT',
      source: 'window:online',
      at: Date.now(),
      eventId: newEventId(),
    });
    emitAuthSessionEvent({
      type: 'RECOVERED',
      source: 'window:online',
      at: Date.now(),
      eventId: newEventId(),
    });
  });

  window.addEventListener('offline', () => {
    emitAuthSessionEvent({
      type: 'OFFLINE_ENTER',
      source: 'window:offline',
      at: Date.now(),
      eventId: newEventId(),
    });
  });
}

export function emitFromApiError(errorOrMessage: unknown, source: string): void {
  const normalized = normalizeApiErrorDetail(errorOrMessage);
  const at = Date.now();

  const kind: ApiErrorKind = normalized.kind;
  if (kind === 'auth' && (normalized.status === 401 || normalized.status === 403)) {
    emitAuthSessionEvent({
      type: 'AUTH_EXPIRED',
      status: normalized.status,
      source,
      at,
      code: normalized.code,
      message: normalized.message,
      eventId: newEventId(),
    });
    return;
  }

  if (kind === 'network') {
    emitAuthSessionEvent({
      type: 'NETWORK_ERROR',
      source,
      at,
      message: normalized.message,
      eventId: newEventId(),
    });
    return;
  }

  if (kind === 'server') {
    emitAuthSessionEvent({
      type: 'SERVER_ERROR',
      status: normalized.status ?? 500,
      source,
      at,
      message: normalized.message,
      eventId: newEventId(),
    });
    return;
  }

  if (kind === 'unknown' && normalized.status === 0) {
    emitAuthSessionEvent({
      type: 'NETWORK_ERROR',
      source,
      at,
      message: normalized.message,
      eventId: newEventId(),
    });
  }
}

export function emitLoginSuccess(source = 'login'): void {
  emitAuthSessionEvent({
    type: 'LOGIN_SUCCESS',
    source,
    at: Date.now(),
    eventId: newEventId(),
  });
}

export function emitLogout(source = 'logout'): void {
  emitAuthSessionEvent({
    type: 'LOGOUT',
    source,
    at: Date.now(),
    eventId: newEventId(),
  });
}

export function emitBrandingRefreshed(source = 'branding'): void {
  emitAuthSessionEvent({
    type: 'BRANDING_REFRESHED',
    source,
    at: Date.now(),
    eventId: newEventId(),
  });
}

